import { Accessor, Setter, createMemo, createSignal } from 'solid-js';
import { Color, HSLColor, date } from 'common';
import { AppointmentInfo, CalendarDateInfo, CalendarLegendInfo, CalendarLegendItem, DateStatus } from 'st-app/api';
import { CalendarDisplayMode } from './st-schedule-grid';

const { truncateTime, startOfWeek, startOfMonth, endOfMonth, addDays, addYears, monthName } = date;

const saturation = 50;
const lightness = 50;
const AppointmentColors = [
    new HSLColor(0, saturation, lightness),
    new HSLColor(64, saturation, lightness),
    new HSLColor(32, saturation, lightness),
    new HSLColor(96, saturation, lightness),
    new HSLColor(160, saturation, lightness),
    new HSLColor(128, saturation, lightness),
    new HSLColor(224, saturation, lightness),
    new HSLColor(192, saturation, lightness),
    new HSLColor(288, saturation, lightness),
    new HSLColor(256, saturation, lightness),
    new HSLColor(352, saturation, lightness),
    new HSLColor(320, saturation, lightness),
]

export class STScheduleGridEvents {
    onDateChanged?: (dt: string) => void;
    onUpdate?: () => void;
    afterAddClicked?: (dt: Date) => void;
    refresh?: () => void;
}

export class CalendarContext {
    calendar: Accessor<CalendarDateInfo[]>;
    baseCalendar: Accessor<CalendarDateInfo[]>;
    setBaseCalendar: Setter<CalendarDateInfo[]>;
    adjCalendar: Accessor<CalendarDateInfo[]>;
    setAdjCalendar: Setter<CalendarDateInfo[]>;
    legends: Accessor<CalendarLegendInfo[]>;
    setLegends: Setter<CalendarLegendInfo[]>;
    enabledLegendItems: Accessor<Record<string, boolean>>;
    setEnabledLegendItems: Setter<Record<string, boolean>>;
    appointments = createSignal<AppointmentInfo[]>([]);
    displayMode: Accessor<CalendarDisplayMode>
    setDisplayMode: Setter<CalendarDisplayMode>
    day: Accessor<Date>
    setDay: Setter<Date>
    hoveredDay: Accessor<Date | undefined>
    setHoveredDay: Setter<Date | undefined>
    isEnabled = createSignal(true);
    minCellHeight = createSignal('');

    colorIndices: Map<string, Color> = new Map<string, Color>();
    colorIndex: number = 0;
    afterAddClicked: boolean;
    events: STScheduleGridEvents

    constructor(events?: STScheduleGridEvents, afterAddClicked?: boolean) {
        this.events = events ?? new STScheduleGridEvents();
        this.afterAddClicked = afterAddClicked ?? false;
        [this.displayMode, this.setDisplayMode] = createSignal<CalendarDisplayMode>('Week');
        [this.baseCalendar, this.setBaseCalendar] = createSignal<CalendarDateInfo[]>([]);
        [this.adjCalendar, this.setAdjCalendar] = createSignal<CalendarDateInfo[]>([]);
        [this.legends, this.setLegends] = createSignal<CalendarLegendInfo[]>([]);
        [this.enabledLegendItems, this.setEnabledLegendItems] = createSignal<Record<string, boolean>>({});
        [this.day, this.setDay] = createSignal(date.today());
        [this.hoveredDay, this.setHoveredDay] = createSignal<Date>();

        this.calendar = createMemo(() => this.baseCalendar().concat(this.adjCalendar()));
    }

    getColor(colorIndex: number) {
        return new HSLColor(colorIndex, saturation, lightness);
    }

    nextColor(category: string | undefined = undefined) {
        if (category) {
            if (!this.colorIndices.has(category)) {
                this.colorIndices.set(category, this.nextColor());
            }
            return this.colorIndices.get(category)!;
        }
        else {
            return AppointmentColors[this.colorIndex++ % AppointmentColors.length];
        }
    }

    getAppointments(dt: Date) {
        const dayTime = date.truncateTime(dt).getTime();
        return this.appointments[0]()
            .filter(a => dayTime >= date.truncateTime(a.start).getTime()
                && dayTime < date.addDays(date.truncateTime(a.end), 1).getTime());
    }

    firstDate() {
        let first: Date | undefined;
        const ranges = this.calendar();

        for (const range of ranges) {
            const start = date.truncateTime(date.dateFromString(range.startDate || ''));
            if (range.status !== DateStatus.Inactive
                && (!first || start.getTime() < first.getTime())) {
                first = start;
            }
        }

        return first;
    }

    lastDate() {
        let last: Date | undefined;
        const ranges = this.calendar();

        for (const range of ranges) {
            const end = date.truncateTime(date.dateFromString(range.endDate || range.startDate || ''));
            if (range.status !== DateStatus.Inactive
                && (!last || end.getTime() > last.getTime())) {
                last = end;
            }
        }

        return last;
    }

    years() {
        const res: number[] = [];

        let start = this.firstDate() ?? date.today();
        let end = this.lastDate() ?? start;

        while (start <= end) {
            res.push(start.getFullYear());
            start = addYears(start, 1);
        }
        return res;
    }

    months() {
        const res: Date[] = [];

        let start = date.startOfMonth(this.firstDate() ?? date.today());
        let end = date.endOfMonth(this.lastDate() ?? start);

        while (start <= end) {
            res.push(start);
            start = date.addMonths(start, 1);
        }

        return res;
    }

    weeksFor(day: Date) {
        const res: Date[] = [];

        let start = startOfWeek(startOfMonth(day));
        while (start <= endOfMonth(day)) {
            res.push(addDays(start, day.getDay()));
            start = addDays(start, 7);
        }
        return res;
    }

    weeks() {
        return this.weeksFor(this.day());
    }

    monthEnabled(dt: Date) {
        let start = date.truncateTime(date.startOfMonth(dt));
        const end = date.truncateTime(date.endOfMonth(dt));

        while (start.getTime() < end.getTime()) {
            if (this.dayEnabled(start)) {
                return true;
            }
            start = date.addDays(start, 1);
        }
        return false;
    }

    dayEnabled(dt: Date) {
        let res = false;

        for (const calDay of this.calendar()) {
            if (dt.getTime() >= date.truncateTime(date.dateFromString(calDay.startDate || '')).getTime()
                && dt.getTime() <= date.truncateTime(date.dateFromString(calDay.endDate || calDay.startDate || '')).getTime())     {
       
                if (calDay.status === DateStatus.Active) {
                    res = true;
                }
                else {
                    res = false;
                }

                // we don't break here because later entries might clarify this specific date
            }
        }

        return this.isEnabled[0]() && res;
    }

    categoryInfo(category?: string) {
        for (const legend of this.legends()) {
            for (const it of legend.items) {
                if (it.key === category) {
                    return it;
                }
            }
        }
    }

    addClicked(date: Date) {
        this.setDay(date);
        if (this.events.afterAddClicked) {
            this.events.afterAddClicked(date);
        }
    }

    showDetails(appt: AppointmentInfo) {
        if (window.ST?.OpenViewInDialog && appt.viewID) {
            window.ST.OpenViewInDialog(appt.viewID, appt.ID, {
                REV_RENDER: {
                    PASS_THROUGH: {
                        'ID': appt.ID,
                        'Title': appt.title,
                        'SubTitle': appt.subTitle,
                        'ViewID': appt.viewID,
                        'Category': appt.category,
                        'ColorIndex': appt.colorIndex,
                        'Start': appt.start,
                        'End': appt.end,
                    }
                }
            }, null, { fullscreen: true });
        }
    }
}
